import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import BaseController from 'uplisting-frontend/pods/base/controller';
import { AnalyticsEvents } from 'uplisting-frontend/services/analytics';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import { isSaveDisabled } from 'uplisting-frontend/utils/changeset';
import { isAnyInvalid } from 'uplisting-frontend/validations/helpers';
import { SafeguestStatuses } from 'uplisting-frontend/models/account-configuration';
import {
  safeguestCurrencyRuleTitleKey as currencyRuleTitleKey,
  safeguestUnsupportedCurrencyKey as unsupportedCurrencyKey,
  supportedSafeguestCurrencies,
} from 'uplisting-frontend/utils/safeguest';

const HOURS_IN_DAY = 24;

type ListingProvider = 'disabled' | 'uplisting' | 'safeguest';
type ActiveProvider = Exclude<ListingProvider, 'disabled'>;

const LISTING_PROVIDERS: ListingProvider[] = [
  'disabled',
  'uplisting',
  'safeguest',
];

interface IBulkSettingSnapshot {
  setting: GenericChangeset<ModelRegistry['security-deposit-setting']>;
  amount: ModelRegistry['security-deposit-setting']['amount'];
  enabled: ModelRegistry['security-deposit-setting']['enabled'];
  provider: ModelRegistry['security-deposit-setting']['provider'];
}

interface IChannel {
  identityConfigField:
    | 'airbnb'
    | 'bookingDotCom'
    | 'homeAway'
    | 'uplisting'
    | 'google';
  securityDepositConfigField: string;
  safeguestConfigField: string;
  translation: string;
}

const IDENTITY_COLUMN_BY_CHANNEL = {
  airbnb: 'airbnb',
  bookingDotCom: 'bookingDotCom',
  homeAway: 'homeAway',
  uplisting: 'uplisting',
  google: 'uplisting',
} as const;

export default class ProtectionSettingsController extends BaseController {
  @service pricing!: Services['pricing'];
  @service analytics!: Services['analytics'];
  @service features!: Services['features'];
  @service featureFlags!: Services['feature-flags'];
  @service sentry!: Services['sentry'];

  @service('airbnb-guest-data-policy')
  airbnbGuestDataPolicy!: Services['airbnb-guest-data-policy'];

  @service('repositories/security-deposit-setting')
  securityDepositSettingRepository!: Services['repositories/security-deposit-setting'];

  @tracked
  identityConfiguration!: ModelRegistry['guest-identity-verification-configuration'];

  @tracked properties!: ModelRegistry['property'][];
  @tracked accountConfiguration!: ModelRegistry['account-configuration'];
  @tracked stripeConnections!: ModelRegistry['stripe-connection'][];

  @tracked guestIdentityActivated!: boolean;
  @tracked showActivateGuestIdentityModal = false;

  @tracked
  securityDepositSettings!: ModelRegistry['security-deposit-setting'][];

  @tracked safeguestEmail = this.currentUser.email;

  @tracked securityDepositsActivated!: boolean;
  @tracked showActivateSecurityDepositsModal = false;

  bulkSecurityDepositSetting!: ModelRegistry['bulk-security-deposit-setting'];

  @tracked isBulkChangeRunning = false;
  @tracked bulkAmount = '';
  @tracked bulkEnabled?: boolean;
  @tracked bulkProvider?: ListingProvider;

  @tracked openSections: string[] = [
    'identity',
    'deposits',
    'channels',
    'listings',
  ];

  @tracked pendingDepositChannelField?: string;
  @tracked pendingIdentityChannel?: IChannel;

  channels: IChannel[] = [
    {
      identityConfigField: 'uplisting',
      securityDepositConfigField: 'securityDepositsEnabledUplisting',
      safeguestConfigField: 'safeguestEnabledUplisting',
      translation: 'uplisting',
    },
    {
      identityConfigField: 'bookingDotCom',
      securityDepositConfigField: 'securityDepositsEnabledBookingDotCom',
      safeguestConfigField: 'safeguestEnabledBookingDotCom',
      translation: 'booking_dot_com',
    },
    {
      identityConfigField: 'airbnb',
      securityDepositConfigField: 'securityDepositsEnabledAirbnbOfficial',
      safeguestConfigField: 'safeguestEnabledAirbnbOfficial',
      translation: 'airbnb',
    },
    {
      identityConfigField: 'homeAway',
      securityDepositConfigField: 'securityDepositsEnabledHomeaway',
      safeguestConfigField: 'safeguestEnabledHomeaway',
      translation: 'vrbo',
    },
    {
      identityConfigField: 'google',
      securityDepositConfigField: 'securityDepositsEnabledGoogle',
      safeguestConfigField: 'safeguestEnabledGoogle',
      translation: 'google',
    },
  ];

  @cached
  get hasEnabledSecurityDeposits(): boolean {
    const enabledForAnyChannel = this.channels.some(
      (channel) =>
        this.securityDepositConfiguration[channel.securityDepositConfigField],
    );

    const enabledForAnyProperty = this.securityDepositSettings.some(
      (item) => item.enabled && item.provider === 'uplisting',
    );

    return enabledForAnyProperty || enabledForAnyChannel;
  }

  @cached
  get isSecurityDepositsActive(): boolean {
    return this.hasEnabledSecurityDeposits || this.securityDepositsActivated;
  }

  @cached
  get securityDepositConfiguration(): GenericChangeset<
    ModelRegistry['account-configuration']
  > {
    return this.accountConfigurationRepository.buildChangeset(
      this.currentAccountConfiguration,
    );
  }

  @cached
  get securityDepositSettingChangesets(): GenericChangeset<
    ModelRegistry['security-deposit-setting']
  >[] {
    const {
      securityDepositSettingRepository,
      bulkSecurityDepositSetting: { securityDepositSettings },
    } = this;

    return securityDepositSettings.map((item) =>
      securityDepositSettingRepository.buildChangeset(item),
    );
  }

  @cached
  get selectedReleaseAfterDays(): number | undefined {
    const { securityDepositReleaseAfterHours } =
      this.securityDepositConfiguration;

    if (securityDepositReleaseAfterHours % HOURS_IN_DAY !== 0) {
      return undefined;
    }

    return securityDepositReleaseAfterHours / HOURS_IN_DAY;
  }

  @cached
  get isGuestIdentityActive(): boolean {
    return this.identityConfiguration.isEnabled || this.guestIdentityActivated;
  }

  @cached
  get isSafeguestActive(): boolean {
    return this.accountConfiguration.safeguestConnected;
  }

  get safeguestCurrencies(): string[] {
    return supportedSafeguestCurrencies(
      this.featureFlags.gate.enabled('safeguest_eur'),
    );
  }

  get safeguestUnsupportedCurrencyKey(): string {
    return unsupportedCurrencyKey(
      this.featureFlags.gate.enabled('safeguest_eur'),
    );
  }

  get safeguestCurrencyRuleTitleKey(): string {
    return currencyRuleTitleKey(
      this.featureFlags.gate.enabled('safeguest_eur'),
    );
  }

  @cached
  get hasSafeguestSupportedProperties(): boolean {
    return this.properties.some((property) =>
      this.safeguestCurrencies.includes(property.currency),
    );
  }

  @cached
  get isSafeguestSupported(): boolean {
    return (
      this.currentUser.hasAccessTo('automate.safeguest') &&
      this.hasSafeguestSupportedProperties
    );
  }

  @cached
  get showSafeguestColumn(): boolean {
    return this.isSafeguestSupported && this.isSafeguestActive;
  }

  @cached
  get enabledListingsCount(): number {
    return this.securityDepositSettingChangesets.filter(
      (setting) => this.settingProvider(setting) !== 'disabled',
    ).length;
  }

  @action
  isSectionOpen(section: string): boolean {
    return this.openSections.includes(section);
  }

  @action
  handleToggleSection(section: string): void {
    if (this.openSections.includes(section)) {
      this.openSections = this.openSections.filter((item) => item !== section);
    } else {
      this.openSections = [...this.openSections, section];
    }
  }

  @action
  handleSafeguestEmail(event: Event): void {
    this.safeguestEmail = (event.target as HTMLInputElement).value;
  }

  @action
  isIdentityEnabledForChannel(channel: IChannel): boolean {
    return !!this.identityConfiguration[
      IDENTITY_COLUMN_BY_CHANNEL[channel.identityConfigField]
    ];
  }

  @action
  isSecurityDepositEnabledForChannel(channel: IChannel): boolean {
    return !!this.securityDepositConfiguration[
      channel.securityDepositConfigField
    ];
  }

  @action
  isSafeguestEnabledForChannel(channel: IChannel): boolean {
    return !!this.securityDepositConfiguration[channel.safeguestConfigField];
  }

  @action
  canChangeSafeguestForChannel(channel: IChannel): boolean {
    return this.isSafeguestActive && !this.isIdentityEnabledForChannel(channel);
  }

  @action
  isCurrencySupportedBySafeguest(
    setting: GenericChangeset<ModelRegistry['security-deposit-setting']>,
  ): boolean {
    return this.safeguestCurrencies.includes(setting.property.currency);
  }

  @action
  @waitFor
  async handleSafeguestConnect(): Promise<void> {
    const model = this.accountConfiguration;
    model.safeguestEmail = this.safeguestEmail;
    model.safeguestStatus = SafeguestStatuses.connect;

    try {
      await model.save();

      this.notifications.info(
        'automate_identity_and_deposits.deposits.notifications.safeguest_connected',
      );
    } catch (error) {
      model.safeguestStatus = null;
      this.sentry.captureException(error);
      this.notifications.error();
    }
  }

  @action
  @waitFor
  async handleToggleGuestIdentityVerificationConfiguration(
    channel: IChannel,
  ): Promise<void> {
    await this.airbnbGuestDataPolicy.guard(
      channel.identityConfigField === 'airbnb' ? 'guest_identity' : undefined,
      this.isIdentityEnabledForChannel(channel),
      () => this.#toggleGuestIdentityVerification(channel),
    );
  }

  async #toggleGuestIdentityVerification(channel: IChannel): Promise<void> {
    const { identityConfiguration } = this;
    const { identityConfigField, translation } = channel;
    const prevValue = identityConfiguration[identityConfigField];
    const isEnabled = !prevValue;

    try {
      identityConfiguration[identityConfigField] = isEnabled;

      await identityConfiguration.save();

      const event = isEnabled
        ? AnalyticsEvents.guestIdentityVerificationEnabled
        : AnalyticsEvents.guestIdentityVerificationDisabled;

      const { intl } = this;

      this.notifications.info(
        'automate_identity_and_deposits.identity.notifications.saved',
        {
          status: intl.t(
            `automate_identity_and_deposits.identity.notifications.status.${isEnabled ? 'enabled' : 'disabled'}`,
          ),
          name: intl.t(
            `automate_identity_and_deposits.identity.notifications.channels.${translation}`,
          ),
        },
      );

      this.analytics.trackEvent(event);
    } catch (e: unknown) {
      identityConfiguration[identityConfigField] = prevValue;

      this.#notifyError(e);
    }
  }

  @action
  handleToggleIdentityChannel(channel: IChannel): void {
    const enabling = !this.isIdentityEnabledForChannel(channel);

    if (enabling && !this.isGuestIdentityActive) {
      this.pendingIdentityChannel = channel;
      this.showActivateGuestIdentityModal = true;

      return;
    }

    this.handleToggleGuestIdentityVerificationConfiguration(channel);
  }

  @action
  handleToggleDepositChannel(channel: IChannel): void {
    const enabling = !this.isSecurityDepositEnabledForChannel(channel);

    if (enabling && !this.isSecurityDepositsActive) {
      this.pendingDepositChannelField = channel.securityDepositConfigField;
      this.showActivateSecurityDepositsModal = true;

      return;
    }

    this.handleToggleAccountConfiguration(channel.securityDepositConfigField);
  }

  @action
  handleActivateGuestIdentity(): void {
    this.guestIdentityActivated = true;
    this.showActivateGuestIdentityModal = false;

    this.analytics.trackEvent(AnalyticsEvents.guestIdentityActivated);

    const channel = this.pendingIdentityChannel;
    this.pendingIdentityChannel = undefined;

    if (channel) {
      this.handleToggleGuestIdentityVerificationConfiguration(channel);
    }
  }

  @action
  handleCancelActivateGuestIdentity(): void {
    this.pendingIdentityChannel = undefined;
    this.showActivateGuestIdentityModal = false;
  }

  @action
  handleActivateSecurityDeposits(): void {
    this.securityDepositsActivated = true;
    this.showActivateSecurityDepositsModal = false;

    this.analytics.trackEvent(AnalyticsEvents.securityDepositActivated);

    const field = this.pendingDepositChannelField;
    this.pendingDepositChannelField = undefined;

    if (field) {
      this.handleToggleAccountConfiguration(field);
    }
  }

  @action
  handleCancelActivateSecurityDeposits(): void {
    this.pendingDepositChannelField = undefined;
    this.showActivateSecurityDepositsModal = false;
  }

  // The bulk control reads as active only when every listing already agrees.
  @cached
  get listingProviderForAll(): ListingProvider | undefined {
    return LISTING_PROVIDERS.find((provider) =>
      this.#everyListingUses(provider),
    );
  }

  @cached
  get isEveryListingEnabled(): boolean {
    const total = this.securityDepositSettingChangesets.length;

    return total > 0 && this.enabledListingsCount === total;
  }

  @cached
  get canToggleAllListings(): boolean {
    return (
      this.securityDepositSettingChangesets.length > 0 &&
      (this.isSecurityDepositsActive || this.showSafeguestColumn)
    );
  }

  // The row stages a change rather than applying one, so the toggle and the
  // provider read back what is staged and fall back to what every listing
  // already has.
  @cached
  get bulkEnabledState(): boolean {
    return this.bulkEnabled ?? this.isEveryListingEnabled;
  }

  @cached
  get bulkProviderState(): ListingProvider | undefined {
    return this.bulkProvider ?? this.listingProviderForAll;
  }

  @cached
  get bulkAmountValue(): number | undefined {
    const amount = Number(this.bulkAmount);

    if (this.bulkAmount === '' || Number.isNaN(amount) || amount < 0) {
      return undefined;
    }

    return amount;
  }

  // What the save would write, or undefined to leave every listing's provider
  // as it is.
  @cached
  get stagedProvider(): ListingProvider | undefined {
    return this.bulkProvider;
  }

  // Shown only once the row would actually change something.
  @cached
  get hasBulkChanges(): boolean {
    const changesets = this.securityDepositSettingChangesets;

    if (this.isBulkChangeRunning || changesets.length === 0) {
      return false;
    }

    const { stagedProvider, bulkEnabled, bulkAmountValue } = this;

    if (stagedProvider && !this.#everyListingUses(stagedProvider)) {
      return true;
    }

    if (bulkEnabled === false && !this.#everyListingUses('disabled')) {
      return true;
    }

    if (
      bulkEnabled === true &&
      changesets.some(
        (setting) => this.#bulkEnableProviderFor(setting) !== undefined,
      )
    ) {
      return true;
    }

    return (
      bulkAmountValue !== undefined &&
      changesets.some((setting) => Number(setting.amount) !== bulkAmountValue)
    );
  }

  @action
  handleStageBulkEnabled(): void {
    const enabled = !this.bulkEnabledState;

    Object.assign(this, {
      bulkEnabled: enabled,
      bulkProvider: enabled ? this.bulkProvider : undefined,
    });
  }

  @action
  handleStageBulkProvider(provider: ListingProvider): void {
    Object.assign(this, { bulkProvider: provider, bulkEnabled: true });
  }

  @action
  handleBulkAmountInput(event: Event): void {
    this.bulkAmount = (event.target as HTMLInputElement).value;
  }

  @action
  @waitFor
  async handleBulkAmountKeydown(event: KeyboardEvent): Promise<void> {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();

    await this.handleSaveBulk();
  }

  // One pass over every listing, one save. A listing priced in a currency
  // SafeGuest does not cover keeps the provider it had, so "everything to
  // SafeGuest" never strands it on an unusable one.
  @action
  @waitFor
  async handleSaveBulk(): Promise<void> {
    if (!this.hasBulkChanges) {
      return;
    }

    const changesets = this.securityDepositSettingChangesets;
    const { bulkAmountValue } = this;
    const snapshots = this.#snapshot(changesets);

    if (bulkAmountValue !== undefined) {
      changesets.forEach((setting) => {
        setting.amount = bulkAmountValue;
      });

      const validations = await Promise.all(
        changesets.map((setting) => setting.validate('amount')),
      );

      if (validations.some((result) => isAnyInvalid(result))) {
        this.#restore(snapshots);
        this.notifications.error();

        return;
      }
    }

    this.isBulkChangeRunning = true;

    changesets.forEach((setting) => this.#applyBulkChangeTo(setting));

    const saved = await this.#saveChanges(this.bulkSecurityDepositSetting);

    if (!saved) {
      this.#restore(snapshots);
    }

    Object.assign(this, {
      bulkEnabled: undefined,
      bulkProvider: undefined,
      bulkAmount: '',
      isBulkChangeRunning: false,
    });
  }

  @action
  isBulkOptionLocked(provider: ListingProvider): boolean {
    if (this.isBulkChangeRunning) {
      return true;
    }

    if (provider === 'safeguest') {
      return !this.showSafeguestColumn;
    }

    if (provider === 'uplisting') {
      return !this.isSecurityDepositsActive;
    }

    return false;
  }

  #everyListingUses(provider: ListingProvider): boolean {
    const settings = this.securityDepositSettingChangesets;

    return (
      settings.length > 0 &&
      settings.every((setting) => this.settingProvider(setting) === provider)
    );
  }

  @action
  settingProvider(
    setting: GenericChangeset<ModelRegistry['security-deposit-setting']>,
  ): ListingProvider {
    if (setting.enabled === false || setting.provider === null) {
      return 'disabled';
    }

    return setting.provider;
  }

  @action
  @waitFor
  async handleChangeListingProvider(
    setting: GenericChangeset<ModelRegistry['security-deposit-setting']>,
    provider: ListingProvider,
  ): Promise<void> {
    if (this.settingProvider(setting) === provider) {
      return;
    }

    await this.#saveSecurityDepositProvider(setting, provider);
  }

  // A listing whose deposit is switched off has no provider to change, and
  // SafeGuest stays out of reach for an unsupported currency.
  @action
  isListingProviderLocked(
    setting: GenericChangeset<ModelRegistry['security-deposit-setting']>,
    provider: 'uplisting' | 'safeguest',
  ): boolean {
    if (this.settingProvider(setting) === 'disabled') {
      return true;
    }

    if (provider === 'safeguest') {
      return !this.isCurrencySupportedBySafeguest(setting);
    }

    return !this.isSecurityDepositsActive;
  }

  @action
  canToggleListingDeposit(
    setting: GenericChangeset<ModelRegistry['security-deposit-setting']>,
  ): boolean {
    if (this.settingProvider(setting) !== 'disabled') {
      return true;
    }

    return this.#providerToEnable(setting) !== undefined;
  }

  @action
  @waitFor
  async handleToggleListingDeposit(
    setting: GenericChangeset<ModelRegistry['security-deposit-setting']>,
  ): Promise<void> {
    const provider =
      this.settingProvider(setting) === 'disabled'
        ? this.#providerToEnable(setting)
        : 'disabled';

    if (!provider) {
      return;
    }

    await this.#saveSecurityDepositProvider(setting, provider);
  }

  #providerToEnable(
    setting: GenericChangeset<ModelRegistry['security-deposit-setting']>,
  ): ActiveProvider | undefined {
    if (setting.provider === 'safeguest' && this.#canUseSafeguestFor(setting)) {
      return 'safeguest';
    }

    if (this.isSecurityDepositsActive) {
      return 'uplisting';
    }

    if (this.#canUseSafeguestFor(setting)) {
      return 'safeguest';
    }

    return undefined;
  }

  #bulkEnableProviderFor(
    setting: GenericChangeset<ModelRegistry['security-deposit-setting']>,
  ): ActiveProvider | undefined {
    if (this.settingProvider(setting) !== 'disabled') {
      return undefined;
    }

    return this.#providerToEnable(setting);
  }

  #canUseSafeguestFor(
    setting: GenericChangeset<ModelRegistry['security-deposit-setting']>,
  ): boolean {
    return (
      this.showSafeguestColumn && this.isCurrencySupportedBySafeguest(setting)
    );
  }

  @action
  @waitFor
  async handleToggleAccountConfiguration(field: string): Promise<void> {
    await this.airbnbGuestDataPolicy.guard(
      field === 'safeguestEnabledAirbnbOfficial' ? 'safeguest' : undefined,
      !!this.securityDepositConfiguration[field],
      () => this.#toggleAccountConfiguration(field),
    );
  }

  async #toggleAccountConfiguration(field: string): Promise<void> {
    const prevValue = this.securityDepositConfiguration[field];

    this.securityDepositConfiguration[field] = !prevValue;

    const saved = await this.#saveChanges(this.securityDepositConfiguration);

    if (!saved) {
      this.securityDepositConfiguration[field] = prevValue;
      this.securityDepositConfiguration.execute();
    }
  }

  @action
  @waitFor
  async handleChangeHoldDuration(
    stripeConnection: ModelRegistry['stripe-connection'],
    extended: boolean,
  ): Promise<void> {
    if (stripeConnection.extendedSecurityDepositHold === extended) {
      return;
    }

    const prevValue = stripeConnection.extendedSecurityDepositHold;

    stripeConnection.extendedSecurityDepositHold = extended;

    try {
      await stripeConnection.save();

      this.notifications.info(
        'automate_identity_and_deposits.deposits.notifications.deposit_settings_saved',
      );
    } catch (e: unknown) {
      stripeConnection.extendedSecurityDepositHold = prevValue;

      this.#notifyError(e);
    }
  }

  @action
  @waitFor
  async handleChangeReleaseAfterDays(days: number): Promise<void> {
    const { securityDepositConfiguration } = this;
    const prevValue =
      securityDepositConfiguration.securityDepositReleaseAfterHours;

    securityDepositConfiguration.securityDepositReleaseAfterHours =
      days * HOURS_IN_DAY;

    const saved = await this.#saveChanges(securityDepositConfiguration);

    if (!saved) {
      securityDepositConfiguration.securityDepositReleaseAfterHours = prevValue;
      securityDepositConfiguration.execute();
    }
  }

  @action
  @waitFor
  async handleChangeDefaultSecurityDepositAmount(): Promise<void> {
    const validations = await this.securityDepositConfiguration.validate(
      'defaultSecurityDepositAmount',
    );

    if (isAnyInvalid(validations)) {
      return;
    }

    try {
      await this.securityDepositConfiguration.save();
    } catch (e: unknown) {
      this.#notifyError(e);

      return;
    }

    this.securityDepositSettings
      .filter((item) => item.amount === 0)
      .forEach((item) => {
        item.amount =
          this.securityDepositConfiguration.defaultSecurityDepositAmount;
      });

    await this.#saveChanges(this.bulkSecurityDepositSetting);
  }

  @action
  @waitFor
  async handleSaveSecurityDepositSetting(
    setting: GenericChangeset<ModelRegistry['security-deposit-setting']>,
  ): Promise<void> {
    const saveDisabled = await isSaveDisabled(setting);

    if (saveDisabled) {
      return;
    }

    setting.execute();

    await this.#saveChanges(this.bulkSecurityDepositSetting);
  }

  async #saveChanges(
    model:
      | ModelRegistry['bulk-security-deposit-setting']
      | GenericChangeset<ModelRegistry['account-configuration']>,
  ): Promise<boolean> {
    try {
      await model.save();

      this.notifications.info(
        'automate_identity_and_deposits.deposits.notifications.deposit_settings_saved',
      );

      return true;
    } catch (e: unknown) {
      this.#notifyError(e);

      return false;
    }
  }

  #notifyError(e: unknown): void {
    const detail = (e as { errors?: { detail?: string }[] })?.errors?.[0]
      ?.detail;

    if (detail) {
      this.notifications.error(detail, true);
    } else {
      this.notifications.error();
    }
  }

  async #saveSecurityDepositProvider(
    setting: GenericChangeset<ModelRegistry['security-deposit-setting']>,
    provider: ListingProvider,
  ): Promise<void> {
    const prevEnabled = setting.enabled;
    const prevProvider = setting.provider;

    this.#updateSecurityDepositProvider(setting, provider);

    const saved = await this.#saveChanges(this.bulkSecurityDepositSetting);

    if (!saved) {
      Object.assign(setting, {
        enabled: prevEnabled,
        provider: prevProvider,
      });

      setting.execute();
    }
  }

  #applyBulkChangeTo(
    setting: GenericChangeset<ModelRegistry['security-deposit-setting']>,
  ): void {
    const { stagedProvider, bulkEnabled } = this;

    if (stagedProvider) {
      const skipSafeguest =
        stagedProvider === 'safeguest' && !this.#canUseSafeguestFor(setting);

      if (!skipSafeguest) {
        this.#updateSecurityDepositProvider(setting, stagedProvider);

        return;
      }
    } else if (bulkEnabled === false) {
      this.#updateSecurityDepositProvider(setting, 'disabled');

      return;
    } else if (bulkEnabled === true) {
      const provider = this.#bulkEnableProviderFor(setting);

      if (provider) {
        this.#updateSecurityDepositProvider(setting, provider);

        return;
      }
    }

    setting.execute();
  }

  #snapshot(
    changesets: GenericChangeset<ModelRegistry['security-deposit-setting']>[],
  ): IBulkSettingSnapshot[] {
    return changesets.map((setting) => ({
      setting,
      amount: setting.amount,
      enabled: setting.enabled,
      provider: setting.provider,
    }));
  }

  #restore(snapshots: IBulkSettingSnapshot[]): void {
    snapshots.forEach(({ setting, amount, enabled, provider }) => {
      Object.assign(setting, { amount, enabled, provider });

      setting.execute();
    });
  }

  // The changeset is saved through its bulk parent, so the change has to be
  // executed onto the underlying record first.
  #updateSecurityDepositProvider(
    setting: GenericChangeset<ModelRegistry['security-deposit-setting']>,
    provider: ListingProvider,
  ): void {
    if (provider === 'disabled') {
      setting.enabled = false;
    } else {
      Object.assign(setting, {
        provider,
        enabled: true,
      });
    }

    setting.execute();
  }

  public setDefaultState(): void {
    Object.assign(this, {
      guestIdentityActivated: this.identityConfiguration.isEnabled,
      securityDepositsActivated: this.hasEnabledSecurityDeposits,
    });
  }
}
